import { createContext, useCallback, useLayoutEffect, useMemo } from 'react';
import { UseQueryResult } from 'react-query';
import { generatePath, useHistory, useLocation } from 'react-router-dom';

import { PATHS } from '../../../shared/constants';
import { useContextFallback } from '../../../shared/hooks';
import { useWorkbookUrl } from '../hooks/use-workbook-url.hook';
import {
  DashboardWorkbook,
  DashboardWorkbooksResponse,
  TableauBaseUrlProps,
  TableauLocationState,
  WorkbookUrl,
} from '../models';
import { useDashboardWorkbooksQuery } from '../queries';
import { getWorkbookUrlByContentUrlAttr } from '../utils';

type DashboardsContextType = {
  categories: string[];
  workbooks: DashboardWorkbook[];
  dashboardWorkbooksQuery: UseQueryResult<DashboardWorkbooksResponse>;
  tableauBaseUrlProps: TableauBaseUrlProps;
  workbookUrl: WorkbookUrl;
  selectedWorkbook?: DashboardWorkbook;
  selectedCategory?: string;
  selectWorkbooksView: (url: WorkbookUrl) => void;
  selectWorkbooksViewByContentUrl: (contentUrl: string) => void;
};

const DashboardsContext = createContext<DashboardsContextType | undefined>(
  undefined
);
DashboardsContext.displayName = 'DashboardsContext';

export const DashboardsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const history = useHistory<TableauLocationState | undefined>();
  const location = useLocation<TableauLocationState | undefined>();
  const workbookUrl = useWorkbookUrl();

  const dashboardWorkbooksQuery = useDashboardWorkbooksQuery();

  const categories = useMemo(
    () => Object.keys(dashboardWorkbooksQuery.data?.workbooksByCategory || {}),
    [dashboardWorkbooksQuery.data]
  );

  const selectedCategory = useMemo(
    () => categories.find((c) => c === workbookUrl.category),
    [categories, workbookUrl.category]
  );

  const workbooks = useMemo(
    () =>
      dashboardWorkbooksQuery.data && selectedCategory
        ? dashboardWorkbooksQuery.data.workbooksByCategory[selectedCategory] ||
          []
        : [],
    [dashboardWorkbooksQuery.data, selectedCategory]
  );

  const selectedWorkbook = useMemo(
    () => workbooks.find((w) => w.name === workbookUrl.workbook),
    [workbooks, workbookUrl.workbook]
  );

  const selectWorkbooksView = useCallback(
    ({ category, filters, workbook }: WorkbookUrl) => {
      const url = generatePath(PATHS.DashboardsWorkbooks, {
        category,
        workbook,
      });
      const queryParams = filters ? `?${filters.toString()}` : '';
      history.push(`${url}${queryParams}`);
    },
    [history]
  );

  const selectWorkbooksViewByContentUrl = useCallback(
    (contentUrl: string) => {
      if (dashboardWorkbooksQuery.data) {
        const workbookUrl = getWorkbookUrlByContentUrlAttr(
          contentUrl,
          dashboardWorkbooksQuery.data.workbooksByCategory
        );
        if (workbookUrl) {
          selectWorkbooksView(workbookUrl);
        }
      }
    },
    [dashboardWorkbooksQuery.data, selectWorkbooksView]
  );

  const tableauBaseUrlProps = useMemo<TableauBaseUrlProps>(() => {
    const { baseUrl = '', siteContentUrl = '' } =
      dashboardWorkbooksQuery.data || {};
    return { baseUrl, siteContentUrl };
  }, [dashboardWorkbooksQuery.data]);

  useLayoutEffect(() => {
    if (
      dashboardWorkbooksQuery.isFetching ||
      location.pathname === PATHS.DashboardsSearch ||
      (categories.length === 0 && location.pathname === PATHS.Dashboards)
    ) {
      return;
    }

    if (!selectedCategory || (workbookUrl.workbook && !selectedWorkbook)) {
      return history.replace(`${PATHS.Dashboards}/${categories[0]}`);
    }
  }, [
    categories,
    dashboardWorkbooksQuery.isFetching,
    history,
    location.pathname,
    selectedCategory,
    selectedWorkbook,
    workbookUrl.workbook,
  ]);

  return (
    <DashboardsContext.Provider
      value={{
        categories,
        workbooks,
        workbookUrl,
        selectedWorkbook,
        selectedCategory,
        dashboardWorkbooksQuery,
        tableauBaseUrlProps,
        selectWorkbooksView,
        selectWorkbooksViewByContentUrl,
      }}
    >
      {children}
    </DashboardsContext.Provider>
  );
};

export const useDashboards = () => useContextFallback(DashboardsContext);
